import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button, Col, Row } from "@folio/stripes/components";
import debounce from "lodash/debounce";
import { RecekSelection } from "./inputs";
import { authorFromAuthority, authorityBiographicalNote, buildAuthorityAuthorLabel, isAuthorityAuthorQuery, isLinkedAuthor, } from "../utils/authorityAuthorUtils";
var SEARCH_DEBOUNCE_MS = 300;
/**
 * Author row of the one-off publisher work specification (DB-804): a typeahead over the NK authorities
 * (natural persons with an ID AUT only) above the first / last name inputs. Picking an authority fills the names,
 * makes them read-only and shows the linked ID AUT with "Zrušit výběr" and the full biographical note of the
 * authority (RECEK-76351); without a pick a new author is typed in.
 */
export var AuthorityAuthorRow = function (_a) {
    var _b, _c, _d;
    var author = _a.author, searchAuthors = _a.searchAuthors, onPick = _a.onPick, onClear = _a.onClear, onRemove = _a.onRemove, renderNameField = _a.renderNameField, _e = _a.linkedPersonIds, linkedPersonIds = _e === void 0 ? [] : _e, _f = _a.disabled, disabled = _f === void 0 ? false : _f;
    var _g = useState(""), query = _g[0], setQuery = _g[1];
    var _h = useState([]), results = _h[0], setResults = _h[1];
    var _j = useState(false), loading = _j[0], setLoading = _j[1];
    var _k = useState(false), searchFailed = _k[0], setSearchFailed = _k[1];
    // id of the latest query typed in; only the response to it may settle results and loading
    var latestRequest = useRef(0);
    var searchRef = useRef(searchAuthors);
    searchRef.current = searchAuthors;
    var linked = isLinkedAuthor(author);
    // built once: a new searchAuthors identity must not cancel the debounce or orphan a running request
    var runSearch = useRef(debounce(function (text, request) {
        var settles = function () { return request === latestRequest.current; };
        searchRef
            .current(text.trim())
            .then(function (found) {
            if (settles()) {
                setResults(found);
                setSearchFailed(false);
            }
        })
            .catch(function () {
            if (settles()) {
                setResults([]);
                setSearchFailed(true);
            }
        })
            .finally(function () {
            if (settles()) {
                setLoading(false);
            }
        });
    }, SEARCH_DEBOUNCE_MS)).current;
    useEffect(function () { return function () {
        runSearch.cancel();
        // ignore any response that arrives after unmount
        latestRequest.current += 1;
    }; }, [runSearch]);
    var handleFilter = function (text) {
        var request = ++latestRequest.current;
        setQuery(text);
        setSearchFailed(false);
        if (!isAuthorityAuthorQuery(text)) {
            runSearch.cancel();
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        runSearch(text, request);
    };
    var offered = results.filter(function (result) { return !linkedPersonIds.includes(result.id); });
    var handlePick = function (personId) {
        var picked = offered.find(function (result) { return result.id === personId; });
        if (picked) {
            onPick(authorFromAuthority(picked));
        }
    };
    var options = offered.map(function (result) { return ({
        value: result.id,
        label: buildAuthorityAuthorLabel(result),
    }); });
    var personId = (_b = author === null || author === void 0 ? void 0 : author.personId) !== null && _b !== void 0 ? _b : "";
    if (linked && !options.some(function (option) { return option.value === personId; })) {
        options.unshift({
            value: personId,
            label: buildAuthorityAuthorLabel({
                id: personId,
                firstName: author === null || author === void 0 ? void 0 : author.firstName,
                lastName: author === null || author === void 0 ? void 0 : author.lastName,
                sourceId: (_c = author === null || author === void 0 ? void 0 : author.sourceId) !== null && _c !== void 0 ? _c : "",
                biographicalNote: author === null || author === void 0 ? void 0 : author.biographicalNote,
            }),
        });
    }
    var emptyMessageId = "ui-br-publishers.publishers.workSpec.selection.authors.authority.minChars";
    if (results.length > 0) {
        emptyMessageId =
            "ui-br-publishers.publishers.workSpec.selection.authors.authority.alreadyLinked";
    }
    else if (isAuthorityAuthorQuery(query)) {
        emptyMessageId =
            "ui-br-publishers.publishers.workSpec.selection.authors.authority.noResults";
    }
    var linkedSourceId = (_d = author === null || author === void 0 ? void 0 : author.sourceId) === null || _d === void 0 ? void 0 : _d.trim();
    var linkedNote = linked ? authorityBiographicalNote(author) : "";
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, null,
            React.createElement(Col, { xs: 8 },
                React.createElement(RecekSelection, { labelId: "ui-br-publishers.publishers.workSpec.selection.label.authors.authority", autoComplete: "off", asyncFilter: true, disabled: disabled, onFilter: handleFilter, dataOptions: options, value: linked ? personId : "", onChange: handlePick, loading: loading, loadingMessage: React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.selection.authors.authority.loading" }), emptyMessage: React.createElement(FormattedMessage, { id: emptyMessageId }), error: searchFailed ? (React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.selection.authors.authority.error" })) : undefined })),
            linked && (React.createElement(Col, { xs: 4 },
                React.createElement("div", { style: { marginTop: "25px" } },
                    React.createElement("strong", null, linkedSourceId ? (React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.authors.linkedAuthority", values: { sourceId: linkedSourceId } })) : (React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.authors.linkedAuthorityWithoutId" }))),
                    " ",
                    React.createElement(Button, { onClick: onClear, disabled: disabled },
                        React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.button.authors.clearAuthority" })))))),
        linkedNote && (React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.authors.biographicalNote", values: { note: linkedNote } }))))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 4 }, renderNameField("firstName", linked)),
            React.createElement(Col, { xs: 4 }, renderNameField("lastName", linked)),
            React.createElement(Col, { xs: 4 },
                React.createElement(Button, { buttonStyle: "danger", onClick: onRemove, style: { marginTop: "25px" }, disabled: disabled },
                    React.createElement(FormattedMessage, { id: "ui-br-publishers.publishers.workSpec.button.delete" }))))));
};
